import type { ProjectDataSubscriptionUpdate } from '@/backend/contracts';
import type { CloudState } from '@/store/cloud-types';
import type { ProjectDataResourceId } from './project-data-requirements';

export type ProjectDataRecord = Record<string, unknown>;

export const isRecord = (value: unknown): value is ProjectDataRecord => (
    value !== null && typeof value === 'object' && !Array.isArray(value)
);

export const asString = (value: unknown, fallback = ''): string => (
    typeof value === 'string' ? value : fallback
);

export const asArray = <T>(value: unknown): T[] => (
    Array.isArray(value) ? value as T[] : []
);

const CASTING_ROLES = new Set(['primary', 'understudy', 'swing', 'standby'] as const);

function isCastingRole(value: unknown): value is CloudState['castAssignments'][number]['role'] {
    return typeof value === 'string'
        && CASTING_ROLES.has(value as CloudState['castAssignments'][number]['role']);
}

function toCastAssignment(value: unknown): CloudState['castAssignments'][number] | null {
    if (!isRecord(value)) return null;

    const id = asString(value.id);
    const characterId = asString(value.characterId);
    const personnelId = asString(value.personnelId);
    if (!id || !characterId || !personnelId) return null;

    return {
        ...value,
        id,
        characterId,
        personnelId,
        role: isCastingRole(value.role) ? value.role : 'primary',
    } as CloudState['castAssignments'][number];
}

export const toCastAssignments = (value: unknown): CloudState['castAssignments'] => (
    asArray<unknown>(value)
        .map(toCastAssignment)
        .filter((assignment): assignment is CloudState['castAssignments'][number] => Boolean(assignment))
);

export const toProjectMeta = (
    value: unknown,
    fallbackTitle = 'Untitled',
): CloudState['projectMeta'] => {
    const record = isRecord(value) ? value : {};
    return {
        title: asString(record.title, fallbackTitle),
        director: asString(record.director),
        sm: asString(record.sm),
        asm: asString(record.asm),
        ...(typeof record.leftLogo === 'string' ? { leftLogo: record.leftLogo } : {}),
        ...(typeof record.rightLogo === 'string' ? { rightLogo: record.rightLogo } : {}),
    };
};

export const toShowStructure = (value: unknown): CloudState['showStructure'] => {
    const record = isRecord(value) ? value : {};
    const showStructure: CloudState['showStructure'] = {
        acts: asArray<CloudState['showStructure']['acts'][number]>(record.acts),
        scenes: asArray<CloudState['showStructure']['scenes'][number]>(record.scenes),
        characters: asArray<CloudState['showStructure']['characters'][number]>(record.characters),
        scriptBlocks: asArray<NonNullable<CloudState['showStructure']['scriptBlocks']>[number]>(record.scriptBlocks),
    };

    if (isRecord(record.characterHints)) {
        showStructure.characterHints = record.characterHints as CloudState['showStructure']['characterHints'];
    }

    return showStructure;
};

function hasOwnField(record: ProjectDataRecord, key: string): boolean {
    return Object.hasOwn(record, key);
}

function toStructureResourceSubscriptionUpdate(record: ProjectDataRecord): Partial<CloudState> {
    const update: Partial<CloudState> = {};

    if (hasOwnField(record, 'rows')) {
        update.rows = asArray<CloudState['rows'][number]>(record.rows);
    }

    if (hasOwnField(record, 'showStructure')) {
        update.showStructure = toShowStructure(record.showStructure);
    }

    return update;
}

export const toMicPackConfig = (value: unknown): CloudState['micPackConfig'] => {
    const record = isRecord(value) ? value : {};
    return {
        totalPacks: typeof record.totalPacks === 'number' ? record.totalPacks : 0,
    };
};

export const projectDataResourceUpdateMap: Partial<Record<ProjectDataResourceId, (record: ProjectDataRecord) => ProjectDataSubscriptionUpdate>> = {
    showStructure: toStructureResourceSubscriptionUpdate,
    runSheet: toStructureResourceSubscriptionUpdate,
    props: record => ({
        props: asArray<CloudState['props'][number]>(record.props),
    }),
    sets: record => ({
        sets: asArray<CloudState['sets'][number]>(record.sets),
    }),
    sound: record => ({
        soundClips: asArray<CloudState['soundClips'][number]>(record.soundClips),
        soundCues: asArray<CloudState['soundCues'][number]>(record.soundCues),
        micAssignments: asArray<CloudState['micAssignments'][number]>(record.micAssignments),
        micSwaps: asArray<CloudState['micSwaps'][number]>(record.micSwaps),
        micPackConfig: toMicPackConfig(record.micPackConfig),
    }),
    lighting: record => ({
        lightingCues: asArray<CloudState['lightingCues'][number]>(record.lightingCues),
        followSpotCues: asArray<CloudState['followSpotCues'][number]>(record.followSpotCues),
        followSpots: asArray<CloudState['followSpots'][number]>(record.followSpots),
    }),
    promptBook: record => ({
        cueAnchors: asArray<CloudState['cueAnchors'][number]>(record.cueAnchors),
        customCues: asArray<CloudState['customCues'][number]>(record.customCues),
    }),
    fly: record => ({
        flyCues: asArray<CloudState['flyCues'][number]>(record.flyCues),
    }),
    projections: record => ({
        projectionCues: asArray<CloudState['projectionCues'][number]>(record.projectionCues),
    }),
    projectMeta: record => ({
        projectMeta: toProjectMeta(record.projectMeta, ''),
        assets: asArray<CloudState['assets'][number]>(record.assets),
    }),
};
